import React, { useEffect, useState, useId } from "react"
import { graphql } from "gatsby"
import { useInnerPageNav } from "terra-one"
import Layout from "../components/Layout/Layout"
import ProductTemplateSections from "../components/ProductTemplateSections/ProductTemplateSections"
import Seo from "../components/SEO/Seo"
import SkipToContent from "../components/SkipToContent/SkipToContent"
import OverlayNoticeConnector from "../components/OverlayNotice/OverlayNoticeConnector"
import { returnActiveOverlayNotice } from "../components/OverlayNotice/OverlayNoticeMethods"
import { returnActivePromotionBanner } from "../components/PromotionBanner/PromotionBannerMethods"
import PromotionBannersWrapper from "../components/PromotionBanner/PromotionBannersWrapper"
import Datalayer from "../components/Datalayer/Datalayer"
import useLocation from "../utils/hooks/useLocation"
import useRegion from "../utils/functions/getRegion"
import useLanguage from "../utils/hooks/useLanguage"
import { formatRegions } from "../utils/config/regionConfig"
import useContentfulCookie from "../utils/hooks/useContentfulCookie"
import RegionBlockPage from "../components/RegionBlockPage/RegionBlockPage"

const ProductPageTemplate = ({ data, pageContext, location }) => {
  const { innerPageNavData, setInnerPageNavData } = useInnerPageNav({
    breadcrumbs: [],
    siblingPages: [],
  })

  const [showJumpNav, updateshowJumpNav] = useState(false)
  const [regionAvailable, setRegionAvailable] = useState(false)
  const [productRegion, setProductRegion] = useState(null)
  const [jumpNavItems, setJumpNavItems] = useState(null)
  const [regions, setRegions] = useState(
    data?.contentfulMainNavigation?.languageSelector?.regions ||
      data?.contentfulProductPage?.regions
  )
  const [regionBlocked, setRegionBlocked] = useState(false)

  const {
    breadcrumbs,
    siblingPages,
    overlayNotice,
    commonSearches,
    locale,
    fullPath,
    slug,
    products,
    contentfulId,
    munchkins,
    currentSpaceId,
    siteIdentifier,
    seoData,
    promotionBanners
  } = pageContext || {}

  // We filter out the products that are not in our locale. We use rawLocale bc it matches what is sent back from Contentful.
  useContentfulCookie(`${useId()}${contentfulId?.contentful_id}`)

  const filteredProducts = products?.filter(prod => {
    return prod?.node?.node_locale === locale
  })
  // We find the product that matches the sku
  const product = filteredProducts?.find(prod => {
    return (
      prod?.node?.sku ===
      data?.contentfulProductPage?.productTemplate?.productSku
    )
  })

  const activeOverlayNotice = returnActiveOverlayNotice(overlayNotice)
  const activePromotionBanners = returnActivePromotionBanner(promotionBanners)

  const globalFooterContent = data?.contentfulGlobalFooter
    ? data?.contentfulGlobalFooter
    : pageContext?.globalFooterFallback

  const getFooterData = () => {
    return {
      ...data?.contentfulFooter,
      ...globalFooterContent,
    }
  }

  const { country } = useLocation(
    regions,
    data?.siteAppData?.internationalRegionLabel,
    data?.siteAppData?.internationalRegionMachineName,
    setRegions,
    data?.contentfulMainNavigation?.languageSelector?.regions
  )
  const formattedRegion = useRegion(
    country,
    regions,
    data?.siteAppData?.internationalRegionLabel,
    data?.siteAppData?.internationalRegionMachineName
  )

  const { languageSettings, saveLanguageSettings } = useLanguage(
    location,
    formattedRegion,
    regions,
    data?.contentfulMainNavigation?.languageSelector?.languages
  )

  useEffect(() => {
    const formatProductRegions = product => {
      const regionUpdate = formatRegions(product?.node?.regions)
      const clonedProduct = JSON.parse(JSON.stringify(product?.node))
      delete clonedProduct["regions"]
      clonedProduct["regions"] = regionUpdate
      setProductRegion(clonedProduct)
    }

    // product is being pulled from corporate master space
    if (product) {
      formatProductRegions(product)

      // else if is if their product is in their own space and not in corporate master. This is a fallback feature
    } else if (
      data?.contentfulProductPage?.productTemplate?.additionalProductInformation
        ?.regions?.length
    ) {
      const regionUpdate = formatRegions(
        data?.contentfulProductPage?.productTemplate
          ?.additionalProductInformation?.regions
      )

      const clonedProduct = JSON.parse(
        JSON.stringify(
          data?.contentfulProductPage?.productTemplate
            ?.additionalProductInformation
        )
      )
      delete clonedProduct["regions"]
      clonedProduct["regions"] = regionUpdate
      setProductRegion(clonedProduct)
    }

    if (formattedRegion && productRegion) {
      if (
        formattedRegion?.region?.toLowerCase() ===
          data?.siteAppData.internationalRegionLabel ||
        formattedRegion?.machineName?.toLowerCase() ===
          data?.siteAppData.internationalRegionMachineName
      ) {
        setRegionAvailable(true)
      } else {
        const checkForAvailability = productRegion.regions?.find(region => {
          return (
            region?.machineName?.toLowerCase() ===
            formattedRegion?.machineName?.toLowerCase()
          )
        })
        if (checkForAvailability) {
          setRegionAvailable(true)
        } else {
          setRegionAvailable(false)
        }
      }
    }
  }, [formattedRegion, product, country])

  useEffect(() => {
    if (breadcrumbs || siblingPages) {
      setInnerPageNavData({ breadcrumbs, siblingPages })
    }
  }, [breadcrumbs, siblingPages, setInnerPageNavData])

  useEffect(() => {
    if (!data) {
      return
    }
    if (
      data?.contentfulProductPage?.jumpNavDisplay === true &&
      data?.contentfulProductPage?.productTemplate
    ) {
      updateshowJumpNav(true)
    }
  }, [data])

  useEffect(() => {
    const checkForRegion = () => {
      // if there are no regions, we will assume the page is available to all regions
      if (
        !data?.contentfulProductPage?.regions ||
        !data?.contentfulProductPage?.regions?.length
      ) {
        setRegionBlocked(false)
        return
      }

      const userRegion = formattedRegion?.machineName
        ? formattedRegion?.machineName
        : formattedRegion?.region
        ? formattedRegion?.region
        : "international"

      // if the region is global or international, we allow the page to be viewed
      if (
        userRegion.toLowerCase() === "global" ||
        userRegion.toLowerCase() === "international"
      ) {
        setRegionBlocked(false)
      } else {
        // we block the page if the region is in the list
        const findRegionInList = data?.contentfulProductPage?.regions?.find(
          region => {
            return (
              region?.machineName?.toLowerCase() ===
                userRegion?.toLowerCase() ||
              region?.name?.toLowerCase() === userRegion?.toLowerCase()
            )
          }
        )
        if (findRegionInList) {
          setRegionBlocked(true)
        } else {
          setRegionBlocked(false)
        }
      }
    }
    if (typeof window !== "undefined") {
      checkForRegion()
    }
  }, [formattedRegion, country, data?.regions])

  const jumpNavType = data?.contentfulProductPage?.jumpNavType
    ? data?.contentfulProductPage?.jumpNavType
    : "Vertical"
  const editedLocale = locale?.toLowerCase() === "en-us" ? "en" : locale

  useEffect(() => {
    const handleContentChange = async event => {
      event.preventDefault()
      await fetch("/__refresh", { method: "POST" })
    }
    window.addEventListener("stackbitObjectsChanged", handleContentChange)

    return () => {
      window.removeEventListener("stackbitObjectsChanged", handleContentChange)
    }
  }, [])

  const productSeoImage = data?.contentfulProductPage?.pageSeo?.opengraphImage?.image 
  ? `https:` + data?.contentfulProductPage?.pageSeo?.opengraphImage?.image?.file?.url
  : null;

  return (
    <>
      <Seo
        title={
          data?.contentfulProductPage?.pageName
            ? data?.contentfulProductPage?.pageName
            : data?.contentfulProductPage?.name
        }
        description={
          data?.contentfulProductPage?.pageName
            ? data?.contentfulProductPage?.pageName
            : data?.contentfulProductPage?.name
        }
        seoCompose={
          seoData
            ? seoData
            : data?.contentfulProductPage?.pageSeo
            ? data?.contentfulProductPage?.pageSeo
            : null
        }
        fullPath={fullPath}
        slug={slug}
        locale={editedLocale}
        appData={data?.siteAppData}
        pageTemplate={"Product"}
        image={productSeoImage}
      />
      <Datalayer data={data} pageContext={pageContext} location={location} />
      <SkipToContent />
      {activeOverlayNotice && (
        <OverlayNoticeConnector
          location={location}
          overlayNotice={activeOverlayNotice}
          locale={editedLocale}
        />
      )}
      {activePromotionBanners?.length > 0 && (
        <PromotionBannersWrapper 
          activePromotionBanners={activePromotionBanners} 
          location={location}
          locale={editedLocale}
          pageContext={pageContext} 
        />
      )}
      <Layout
        region={formattedRegion}
        key={data?.contentfulProductPage?.productTemplate?.productSku}
        innerPageNavData={innerPageNavData}
        rawNavigationData={data?.contentfulMainNavigation}
        rawFooterData={getFooterData()}
        rawFlyoutData={data?.contentfulFlyout}
        rawFlyoutCartData={data?.contentfulFlyoutCart}
        locale={editedLocale}
        location={location}
        commonSearches={commonSearches}
        rawAppData={data?.siteAppData}
        languageSettings={languageSettings}
        saveLanguageSettings={saveLanguageSettings}
        munchkins={munchkins}
        homepageSlug={pageContext?.homepage}
        siteIdentifier={siteIdentifier}
        products={products}
        seoStructuredData={data?.contentfulProductPage?.json}
        regions={regions}
        showJumpNav={showJumpNav}
        jumpNavItems={jumpNavItems}
        jumpNavType={jumpNavType}
        currentSpaceId={currentSpaceId}
        siteNavigationData={data?.contentfulSiteNavigation}
      >
        <div data-sb-object-id={data?.contentfulProductPage?.contentfulId}>
          <section
            className="luna-layout__sections"
            id="page-content"
            data-sb-field-path="productTemplate"
          >
            {(data?.contentfulProductPage?.productTemplate &&
              regionAvailable) ||
              (!regionBlocked && (
                <ProductTemplateSections
                  sections={data?.contentfulProductPage?.productTemplate}
                  location={location}
                  locale={editedLocale}
                  product={product}
                  rawLocale={locale}
                  region={formattedRegion}
                  currentSpaceId={
                    currentSpaceId
                      ? currentSpaceId
                      : pageContext?.currentSpaceId
                  }
                  products={products}
                  siteIdentifier={siteIdentifier}
                  setJumpNavItems={setJumpNavItems}
                  jumpNavItems={jumpNavItems}
                  jumpNavType={jumpNavType}
                  showJumpNav={showJumpNav}
                  jumpNavTitle={
                    data?.contentfulProductPage?.jumpNavTitle
                      ? data?.contentfulProductPage?.jumpNavTitle
                      : "This Page "
                  }
                />
              ))}
            {}
            {!regionAvailable && regionBlocked && (
              <RegionBlockPage
                pageContent={data?.contentfulRegionBlockPage?.pageContent}
              />
            )}
          </section>
        </div>
      </Layout>
    </>
  )
}

export default ProductPageTemplate

export const query = graphql`
  query ProductPageTemplate(
    $id: String
    $locale: String
    $currentSpaceId: String
    $appDataID: String
    $corporateSpaceId: String
  ) {
    siteAppData: contentfulSiteApplicationData(subsites: { eq: $appDataID }, node_locale: { eq: $locale }) {
      ...SiteApplicationData
    }
    contentfulFooter(
      spaceId: { eq: $currentSpaceId }
      node_locale: { eq: $locale }
      subsites: { eq: $appDataID }
    ) {
      ... on ContentfulFooter {
        ...Footer
      }
    }
    contentfulGlobalFooter(
      spaceId: { eq: $corporateSpaceId }
      node_locale: { eq: $locale }
      metadata: {
        tags: { elemMatch: { name: { eq: "corporate-global-footer" } } }
      }
    ) {
      ... on ContentfulGlobalFooter {
        ...GlobalFooter
      }
    }
    contentfulMainNavigation(
      spaceId: { eq: $currentSpaceId }
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulMainNavigation {
        ...MainNavigation
      }
    }
    contentfulProductPage(
      id: { eq: $id }
      spaceId: { eq: $currentSpaceId }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulProductPage {
        ...ProductPage
      }
    }
    contentfulRegionBlockPage(
      spaceId: { eq: $currentSpaceId }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulRegionBlockPage {
        ...RegionBlockPage
      }
    }
    contentfulSiteNavigation(
      spaceId: { eq: $currentSpaceId }
      node_locale: { eq: $locale }
    ) {
      ...SiteNavigation
    }
    contentfulFlyout(
      spaceId: { eq: $currentSpaceId }
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulFlyout {
        ...Flyout
      }
    }
    contentfulFlyoutCart(
      spaceId: { eq: $currentSpaceId }
      subsites: { eq: $appDataID }
      node_locale: { eq: $locale }
    ) {
      ... on ContentfulFlyoutCart {
        ...FlyoutCart
      }
    }
  }
`
